import { useAtom, useSetAtom } from 'jotai';
import { Button, RadioButton, RadioButtonGroup, Tag } from '@carbon/react';
import { Edit, TrashCan } from '@carbon/icons-react';
import { aoiEditingAtom, spatialFilterAtom } from './atoms';
import type { SpatialMode } from './types';
import styles from './spatial-filter-controls.module.scss';

/** The Global Map Filter modes, in display order (mock §4b). */
const SPATIAL_MODES: { mode: SpatialMode; label: string }[] = [
  { mode: 'packageArea', label: 'Package Area' },
  { mode: 'extent', label: 'Use Visible Map Extent' },
  { mode: 'aoi', label: 'Draw AOI Polygon' },
];

/**
 * Global Map Filter mode control — a vertical Carbon `RadioButtonGroup` over the three
 * spatial modes (Package Area / Use Visible Map Extent / Draw AOI Polygon). Writes
 * `spatialFilterAtom`'s mode; the reducer clears any captured polygon on a mode change.
 *
 * In AOI mode the polygon is drawn directly on the map (no toolbar icons — see
 * `DrawAoiControl`) and managed from here: once a polygon exists, an applied-area card
 * shows below the radios with **Edit** (enters vertex editing on the map via the
 * controlled `aoiEditingAtom`) and **Delete** (clear the polygon → the map re-arms a
 * fresh draw). The card is intentionally STATIC across editing — Edit just initiates map
 * editing; the panel doesn't reshuffle. `aoiEditingAtom` is a two-way mirror of the map's
 * edit mode (it clears itself when the user clicks away, so a later Edit re-triggers), and
 * the polygon stays the single source of truth in `spatialFilterAtom`.
 *
 * @return {JSX.Element} The spatial-mode radio group + the applied-AOI card.
 */
export function SpatialFilterControls() {
  const [{ mode, polygon }, dispatch] = useAtom(spatialFilterAtom);
  const setEditing = useSetAtom(aoiEditingAtom);

  const deleteAoi = () => {
    dispatch({ type: 'setPolygon', polygon: null });
    setEditing(false);
  };

  return (
    <div className={styles.controls}>
      <RadioButtonGroup
        name="eval3-spatial-mode"
        legendText="Map filter mode"
        orientation="vertical"
        valueSelected={mode}
        // Carbon types the selected value as `string | number`; narrow it back to a
        // SpatialMode via the source list (no cast) so a stray radio value can't
        // reach the reducer.
        onChange={(value) => {
          const next = SPATIAL_MODES.find((m) => m.mode === value)?.mode;
          if (next) {
            dispatch({ type: 'setMode', mode: next });
          }
        }}
      >
        {SPATIAL_MODES.map(({ mode: value, label }) => (
          <RadioButton
            key={value}
            id={`eval3-spatial-${value}`}
            value={value}
            labelText={label}
          />
        ))}
      </RadioButtonGroup>

      {/* Applied AOI — shown once a polygon is drawn. Static across editing: Edit just
          enters vertex editing on the map (the panel doesn't change); Delete clears it
          (the map then re-arms a fresh draw). */}
      {mode === 'aoi' && polygon && (
        <div className={styles.appliedAoi}>
          <div className={styles.appliedAoiHeader}>
            <span className={styles.appliedAoiLabel}>AOI Polygon</span>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Edit}
              iconDescription="Edit area"
              tooltipPosition="bottom"
              onClick={() => setEditing(true)}
            />
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={TrashCan}
              iconDescription="Delete area"
              tooltipPosition="bottom"
              onClick={deleteAoi}
            />
          </div>
          <Tag type="blue" size="sm">
            Polygon drawn
          </Tag>
        </div>
      )}
    </div>
  );
}
